import { alerts } from "@features/alerts/alerts"
import { flyingCarpet } from "@features/flying-carpet/useFlyingCarpet"
import { tv } from "@utils/tw"
import { useEffect, useState } from "react"
import { proxy } from "valtio"
import { FerveAPI } from "./ferve-api-client"
import { useFerve } from "./FerveProvider"

type FerveEmbedArgs = {
  itemHash: string
}

export function useFerveEmbed(args: FerveEmbedArgs) {
  const client = useFerve()
  const [isOpen, setOpen] = useState(false)
  const [isLoading, setLoading] = useState(false)
  const [manager, setManager] = useState<FerveIframeManager | null>(null)
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null)

  useEffect(() => {
    if (isOpen && targetElement) {
      let destroyed = false
      setLoading(true)
      const manager = new FerveIframeManager(client, args.itemHash, targetElement!)
      manager.init().then(() => {
        if (destroyed) return
        // Timeout to wait for resizing to occur
        setTimeout(() => {
          if (destroyed) return
          setLoading(false)
          setManager(manager)
        }, 300)
      })
      return () => {
        destroyed = true
        // Timeout to wait for animations to finish (bit hacky...)
        setTimeout(() => {
          manager.destroy()
        }, 800)
      }
    }
  }, [client, args.itemHash, isOpen])

  return {
    isOpen,
    isLoading,
    isReady: isOpen && !isLoading,
    open: () => {
      setLoading(true)
      setOpen(true)
    },
    close: () => {
      setOpen(false)
      setLoading(false)
    },
    child: <div data-container ref={setTargetElement} />,
  }
}

export class FerveIframeManager {
  iframe!: HTMLIFrameElement
  contentWindow!: Window
  contentDocument!: Document

  wrapper!: HTMLDivElement

  state = proxy({
    loaded: false,
    scrollHeight: 0,
  })

  disposers: (() => void)[] = []

  constructor(
    public client: FerveAPI,
    public itemHash: string,
    public container: HTMLElement,
  ) {}

  async init() {
    this.iframe = document.createElement("iframe")
    this.iframe.className = createStyles().iframe()
    this.container.appendChild(this.iframe)

    const ferveApiScript = `${this.client.url("/js/ftapi.js")}?h=${
      this.client.conn.hash
    }&u=${this.client.url("/")}&s=${this.client.url("/")}&location=false`

    const ferveMainScript = this.client.url("/js/ferve.js") + "?v=1.5.11"

    const iframeSrc = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <base href="${document.location.origin}">
          <meta name="viewport" content="width=device-width,initial-scale=1.0,user-scalable=no" />
          <meta http-equiv="X-UA-Compatible" content="IE=edge,chrome=1" />
          <script src="${ferveApiScript}" id="ft_ftapiJS"></script>
          <script src="${ferveMainScript}" id="ft_JS"></script>
          <script type="text/javascript">
            ferve.itemHash = ${JSON.stringify(this.itemHash)}
          </script>
          <link rel="stylesheet" href="${(await import("../../styles/ferve.css?url")).default}" />
        </head>
        <body class="pricing-widget-contents branded">
          <div id="ferve_embed_container">
            <div id='ft_eventPricing_${this.itemHash}'></div>
          </div>
        </body>
      </html>
    `

    await new Promise<void>((resolve) => {
      this.iframe.onload = () => {
        this.contentWindow = this.iframe.contentWindow!
        this.contentDocument = this.contentWindow.document
        this.install()

        if (this.contentDocument?.readyState === "complete") {
          resolve()
        } else {
          this.contentDocument?.addEventListener("readystatechange", () => {
            if (this.contentDocument.readyState === "complete") {
              resolve()
            }
          })
        }
      }

      this.iframe.srcdoc = iframeSrc
    })
  }

  private onDispose(disposer: () => void) {
    this.disposers.push(disposer)
  }

  public destroy() {
    this.iframe.remove()
    this.disposers.forEach((d) => d())
    this.disposers = []
  }

  install() {
    const win = this.contentWindow as Window & { ferve: any }
    const doc = this.contentDocument
    this.wrapper = doc.getElementById("ferve_embed_container") as HTMLDivElement

    // win.ferve.log = (...args: any[]) => {}

    const debugLog = (message: string, ...args: any[]) => {
      console.debug("%c\x1B[41;93;4m" + message + "\x1B[m", message, ...args)
    }

    const _toggleDisplay = win.ferve.toggleDisplay
    win.ferve.toggleDisplay = (name: string, display: string, ignoreClass: any) => {
      debugLog("toggleDisplay", { name, display, ignoreClass })
      if (name === "ft_fcBG") return
      if (name === "ft_ferveCart") {
        if (display !== "none") {
          this.client.cart.refreshCart()
          flyingCarpet.launch("cart")
        }
        return
      }
      _toggleDisplay(name, display, ignoreClass)
    }
    win.ferve.notification.show = (style: any, colour: any, message: any) => {
      debugLog("ferve.notification.show()", { style, colour, message })
      if (message === "<!-- PV2 -->") {
        message = "This ticket is not currently available."
      }
      alerts.displayAlert({
        title: colour === "red" ? "Oops" : "Notice",
        description: message,
        canDismiss: true,
        confirmButton: "Okay",
      })
    }

    const updateHeight = () => {
      const height = doc.scrollingElement!.scrollHeight
      this.iframe.style.height = height + "px"
    }

    const mo = new MutationObserver((e) => {
      updateHeight()
    })
    mo.observe(this.contentDocument, {
      subtree: true,
      childList: true,
      attributeFilter: ["style", "class", "id"],
      attributes: true,
    })

    const ro = new ResizeObserver((entries) => {
      updateHeight()
    })
    updateHeight()
    ro.observe(this.wrapper)
    this.onDispose(() => ro.disconnect())
  }
}

const createStyles = tv({
  base: "",
  slots: {
    iframe: "overflow-auto border-0 w-full",
  },
})
